import React, { Fragment, useState } from "react"
import PropTypes from "prop-types"
import {
    useTable,
    useGlobalFilter,
    useAsyncDebounce,
    useSortBy,
    useFilters,
    useExpanded,
    usePagination,
} from "react-table"
import { Table, Row, Col, Button, Input } from "reactstrap"
import { Filter, DefaultColumnFilter } from "./filters"
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter"
import { getUserAuthorized } from "helpers/backend_helper"
import FullPageLoader from "./FullPageLoader"
import AccessControlWrapper from "components/Common/PermissionButton"
import CButton from "components/Common/CButton"

// Define a default UI for filtering
function GlobalFilter({
    preGlobalFilteredRows,
    globalFilter,
    setGlobalFilter,
    isJobListGlobalFilter,
}) {
    const count = preGlobalFilteredRows.length
    const [value, setValue] = React.useState(globalFilter)
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined)
    }, 200)

    return (
        <React.Fragment>
            <Col md={6}>
                <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
                    <div className="position-relative">
                        <label htmlFor="search-bar-0" className="search-label">
                            <span id="search-bar-0-label" className="sr-only">
                                Search this table
                            </span>
                            <input
                                onChange={e => {
                                    setValue(e.target.value)
                                    onChange(e.target.value)
                                }}
                                id="search-bar-0"
                                type="text"
                                className="form-control"
                                placeholder={`${count} records...`}
                                value={value || ""}
                            />
                        </label>
                        <i className="bx bx-search-alt search-icon"></i>
                    </div>
                </div>
            </Col>
            {isJobListGlobalFilter && <JobListGlobalFilter />}
        </React.Fragment>
    )
}

const TableContainer = ({
    columns,
    data,
    isGlobalFilter,
    isJobListGlobalFilter,
    isAddOptions,
    handleCustomerClick,
    isAddCustList,
    customPageSize,
    className,
    customPageSizeOptions,
    pageSizeChange,
    isAdd,
    isAddButtonText,
    rowClick,
    handleAddButtonClick,
    clientPagination,
    // New authentication props
    protectedRowClick = false,
    contextUri,
    actionType = "get",
    // Server-side pagination + refresh props
    onPageChange,
    currentPage,
    hasNextPage,
    onRefresh,
    refreshContextUri,
    refreshActionType = "get",
    history,
}) => {
    const [isCheckingAccess, setIsCheckingAccess] = useState(false)

    // Protected row click handler with authentication
    const handleProtectedRowClick = async (e, row) => {
        e.preventDefault()
        e.stopPropagation()

        if (isCheckingAccess) return

        setIsCheckingAccess(true)

        try {
            const hasAccess = await getUserAuthorized({
                headers: {
                    "input-parameters": {
                        requestURI: contextUri,
                        requestMethod: actionType,
                    },
                },
            })

            const { code, desc, type } = hasAccess

            if (
                type === "USER_AUTHORIZATION_STATUS" &&
                desc === "true" &&
                code === "200"
            ) {
                // Access granted - call the original rowClick handler
                if (rowClick) {
                    rowClick(e, row)
                }
            } else {
                throw "You do not have permission to perform this action"
            }
        } catch (error) {
            console.error("Access check failed:", error)
            alert("You do not have permission to perform this action.")
        } finally {
            setIsCheckingAccess(false)
        }
    }

    // Determine which row click handler to use
    const effectiveRowClick = protectedRowClick ? handleProtectedRowClick : rowClick

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
        preGlobalFilteredRows,
        setGlobalFilter,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn: { Filter: DefaultColumnFilter },
            initialState: {
                pageIndex: 0,
                pageSize: customPageSize,
            },
            //   manualPagination: true,
            //   pageCount: 1,
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        useExpanded,
        usePagination
    )

    const generateSortingIndicator = column => {
        return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""
    }

    const onChangeInSelect = event => {
        setPageSize(Number(event.target.value))
    }

    const onChangeInInput = event => {
        const page = event.target.value ? Number(event.target.value) - 1 : 0
        gotoPage(page)
    }
    return (
        <Fragment>
            <Row className="mb-2">
                {pageSizeChange && (
                    <Col md={customPageSizeOptions ? 2 : 1}>
                        <select
                            className="form-select"
                            value={pageSize}
                            onChange={onChangeInSelect}
                        >
                            {[10, 20, 30, 40, 50].map(pageSize => (
                                <option key={pageSize} value={pageSize}>
                                    Show {pageSize}
                                </option>
                            ))}
                        </select>
                    </Col>
                )}

                {isGlobalFilter && (
                    <GlobalFilter
                        preGlobalFilteredRows={preGlobalFilteredRows}
                        globalFilter={state.globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        isJobListGlobalFilter={isJobListGlobalFilter}
                    />
                )}
                {isAddOptions && (
                    <Col sm="7">
                        <div className="text-sm-end">
                            <Button
                                type="button"
                                color="success"
                                className="btn-rounded  mb-2 me-2"
                                onClick={handleOrderClicks}
                            >
                                <i className="mdi mdi-plus me-1" />
                                Add New Order
                            </Button>
                        </div>
                    </Col>
                )}
                {isAdd && (
                    <Col md="6">
                        <div className="text-sm-end">
                            <Button
                                type="button"
                                color="primary"
                                className="btn mb-2 me-2"
                                onClick={handleAddButtonClick}
                            >
                                <i className="mdi mdi-plus-circle-outline me-1" />
                                {isAddButtonText}
                            </Button>
                        </div>
                    </Col>
                )}
                {isAddCustList && (
                    <Col sm="7">
                        <div className="text-sm-end">
                            <Button
                                type="button"
                                color="success"
                                className="btn-rounded mb-2 me-2"
                                onClick={handleCustomerClick}
                            >
                                <i className="mdi mdi-plus me-1" />
                                New Customers
                            </Button>
                        </div>
                    </Col>
                )}
            </Row>

            <div className="table-responsive react-table">
                <Table bordered striped hover {...getTableProps()} className={`table-striped ${className || ''}`}>
                    <thead className="table-light table-nowrap">
                        {headerGroups.map(headerGroup => (
                            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th key={column.id}>
                                        <div
                                            className={column.canFilter ? "mb-2" : ""}
                                            {...column.getSortByToggleProps()}
                                        >
                                            {column.render("Header")}
                                            {generateSortingIndicator(column)}
                                        </div>
                                        {column.canFilter ? <Filter column={column} /> : null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {page.map(row => {
                            prepareRow(row)
                            return (
                                <Fragment key={row.getRowProps().key}>
                                    <tr
                                        {...row.getRowProps()}
                                        onClick={effectiveRowClick ? e => effectiveRowClick(e, row) : null}
                                        style={{
                                            cursor: protectedRowClick && effectiveRowClick ? "pointer" : "default"
                                        }}
                                    >
                                        {row.cells.map(cell => {
                                            return (
                                                <td key={cell.id} {...cell.getCellProps()} className="align-middle">
                                                    {cell.render("Cell")}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                </Fragment>
                            )
                        })}
                        {page.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className="text-center">
                                    No records found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
            {clientPagination ? (
                <Row className="justify-content-md-end justify-content-center align-items-center">
                    <Col className="col-md-auto">
                        <div className="d-flex gap-1">
                            <Button
                                color="primary"
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            >
                                {"<<"}
                            </Button>
                            <Button
                                color="primary"
                                onClick={previousPage}
                                disabled={!canPreviousPage}
                            >
                                {"<"}
                            </Button>
                        </div>
                    </Col>
                    <Col className="col-md-auto d-none d-md-block">
                        Page{" "}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>
                    </Col>
                    <Col className="col-md-auto">
                        <Input
                            type="number"
                            min={1}
                            style={{ width: 70 }}
                            max={pageOptions.length}
                            defaultValue={pageIndex + 1}
                            onChange={onChangeInInput}
                        />
                    </Col>

                    <Col className="col-md-auto">
                        <div className="d-flex gap-1">
                            <Button
                                color="primary"
                                onClick={nextPage}
                                disabled={!canNextPage}
                            >
                                {">"}
                            </Button>
                            <Button
                                color="primary"
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            >
                                {">>"}
                            </Button>
                        </div>
                    </Col>
                </Row>
            ) : null}
            {onPageChange && (
                <Row>
                    <Col>
                        <div className="d-flex justify-content-end align-items-center mt-3">
                            {onRefresh && (
                                <AccessControlWrapper
                                    actionType={refreshActionType}
                                    contextUri={refreshContextUri}
                                    history={history}
                                >
                                    <CButton.Secondary
                                        icon="mdi mdi-refresh"
                                        className="me-2"
                                        onClick={onRefresh}
                                        title="Refresh"
                                        style={{ lineHeight: 1, padding: "0.65rem 0.75rem" }}
                                    />
                                </AccessControlWrapper>
                            )}
                            <CButton.Primary
                                text="Previous"
                                disabled={currentPage === 1}
                                onClick={() => onPageChange(currentPage - 1)}
                            />
                            <span className="mx-2">Page {currentPage}</span>
                            <CButton.Primary
                                text="Next"
                                disabled={!hasNextPage}
                                onClick={() => onPageChange(currentPage + 1)}
                            />
                        </div>
                    </Col>
                </Row>
            )}
            <FullPageLoader show={isCheckingAccess} />
        </Fragment>
    )
}

TableContainer.propTypes = {
    preGlobalFilteredRows: PropTypes.any,
}

export default TableContainer
